import { toast } from "react-toastify";
import { markAttendance } from "../../../context/actions/attendance";
import { fmtDay, fmtDayTime, fmtRange, fmtTime, isToday, useAt, useCountdown } from "../home/time";
import Icon from "./Icon";
import RecordingPlayer, { ATTEND_PERCENT } from "./RecordingPlayer";
import SubmissionForm, { SubmittedCard } from "./SubmissionForm";

const gcalUrl = (l) => {
  const f = (iso) => new Date(iso).toISOString().replace(/[-:]|\.\d{3}/g, "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(l.title)}&dates=${f(l.start_time)}/${f(l.end_time)}&details=${encodeURIComponent("E1X Academy live class")}`;
};
const hrs = (l) => { const h = (new Date(l.end_time) - new Date(l.start_time)) / 3600000; return h >= 1 ? `${+h.toFixed(1)} ${h === 1 ? "hour" : "hours"}` : `${Math.round(h * 60)} min`; };

/** Top of the lesson (prototype `.stage`): live / upcoming / recording coming soon / recording. */
function Stage({ lesson, onChanged }) {
  const countdown = useCountdown(lesson.start_time);
  // Join opens 15 minutes before class: reload then so the button appears.
  useAt(lesson.link ? null : lesson.join_opens_at, onChanged);
  const makeup = lesson.type === "makeup";
  const untracked = makeup || lesson.tracked === false; // makeup and orientation classes: no attendance
  const today = isToday(lesson.start_time);

  const mark = async (silent) => {
    if (lesson.attended || untracked) return;
    const res = await markAttendance(lesson.id);
    if (res.ok) { toast.success("Attendance marked · lesson complete"); onChanged(); }
    else if (!silent) toast.error(res.message);
  };
  const join = () => { if (lesson.link) window.open(lesson.link, "_blank", "noopener,noreferrer"); mark(true); };

  if (lesson.state === "recording") return <RecordingPlayer lesson={lesson} onProgress={onChanged} />;
  if (lesson.state === "pending") {
    return (
      <section className="stage pending">
        <span className="label">Recording Coming Soon</span>
        <h2>{lesson.title}</h2>
        <span>Class held {fmtDay(lesson.start_time)}, {fmtRange(lesson.start_time, lesson.end_time)}</span>
        <small>The class has ended. The recording will be posted here, and you'll get a notification when it's up.</small>
      </section>
    );
  }
  if (lesson.state === "live") {
    return (
      <section className="stage">
        <span className="label"><span className="live-dot" />{makeup ? "Makeup class · live now" : `Live now · ${hrs(lesson)}`}</span>
        <h2>{lesson.title}</h2>
        <span>{fmtRange(lesson.start_time, lesson.end_time)}</span>
        <div className="row-btns">
          {lesson.link && <button className="btn primary" type="button" onClick={join}>Join Class</button>}
        </div>
        {!untracked && <small>{lesson.attended ? "✓ Attendance marked" : "Joining the class marks your attendance. Missed it? Watching 60% of the recording counts too."}</small>}
      </section>
    );
  }
  if (today) {
    return (
      <section className="stage">
        <span className="label"><span className="live-dot" />Live today · {hrs(lesson)}</span>
        <h2>{lesson.title}</h2>
        <span>{fmtRange(lesson.start_time, lesson.end_time)}</span>
        {countdown && <div className="big">{countdown}</div>}
        <div className="row-btns">
          {lesson.link ? <button className="btn primary" type="button" onClick={join}>Join Class</button>
            : lesson.join_opens_at && <span className="btn ghost" aria-disabled="true" style={{ cursor: "default" }}>Join opens {fmtTime(lesson.join_opens_at)}</span>}
          <a className="btn ghost" href={gcalUrl(lesson)} target="_blank" rel="noopener noreferrer">Add to Calendar</a>
        </div>
        <small>Join opens 15 minutes before class. Joining marks your attendance.</small>
      </section>
    );
  }
  return (
    <section className="stage upcoming">
      <span className="label">{makeup ? "Upcoming Makeup Class" : "Upcoming Live Class"} · {hrs(lesson)}</span>
      <h2>{lesson.title}</h2>
      <span>{fmtDay(lesson.start_time)}, {fmtRange(lesson.start_time, lesson.end_time)}</span>
      <div className="row-btns"><a className="btn solid" href={gcalUrl(lesson)} target="_blank" rel="noopener noreferrer">Add to Calendar</a></div>
      <small>The join link appears here on the day of class.</small>
    </section>
  );
}

function CompletionBox({ lesson }) {
  const ok = (t, s) => <section className="complete-box ok"><div><strong>✓ {t}</strong><small>{s}</small></div></section>;
  const info = (t, s) => <section className="complete-box"><div><strong>{t}</strong><small>{s}</small></div></section>;
  if (lesson.type === "makeup") return info("Makeup Class", "Attendance isn't tracked for makeup classes.");
  if (lesson.type === "session") return info("Special Session", "An extra session, so it doesn't count toward your attendance. Join live or watch the recording any time.");
  if (lesson.tracked === false) return info("Not Tracked", "Classes in this week aren't tracked, so there's nothing to complete here. Watch the recording any time; it doesn't affect your attendance.");
  if (lesson.attended) return ok("Attendance marked · lesson complete", "You attended this class live.");
  if (lesson.complete) return ok("Attendance marked from the recording · lesson complete", `You watched at least ${lesson.attend_percent ?? ATTEND_PERCENT}% of the recording. It counts toward your attendance just like joining live.`);
  if (lesson.state === "live") return info("Join the class to complete this lesson", "Joining marks your attendance. If you miss it, watch at least 60% of the recording once it's posted.");
  if (lesson.state === "upcoming") return info("How to Complete This Lesson", `Attend the live class on ${fmtDay(lesson.start_time)}. If you miss it, watch at least ${lesson.attend_percent ?? ATTEND_PERCENT}% of the recording once it's posted. That marks your attendance and completes the lesson.`);
  if (lesson.state === "pending") return info("Missed the live class?", `Once the recording is posted, watch at least ${lesson.attend_percent ?? ATTEND_PERCENT}% of it. That marks your attendance and completes the lesson.`);
  return info("Missed the live class? Watch the recording", `Once you've watched ${lesson.attend_percent ?? ATTEND_PERCENT}% of it, your attendance is marked and this lesson is complete. No button to press.`);
}

export default function LessonView({ lesson, prev, next, onOpen, onAssignment, onChanged }) {
  const a = lesson.assignment;
  return (
    <>
      <Stage lesson={lesson} onChanged={onChanged} />
      <div className="detail-h"><span className="kicker">{lesson.weekLabel}{lesson.number ? ` · Lesson ${lesson.number}` : ""}</span><h2>{lesson.title}</h2></div>
      <CompletionBox lesson={lesson} />
      {a && (
        <section className="lesson-assign">
          <div><strong>Assignment from this class: {a.title}</strong>
            <small>{a.submission ? (a.submission.grade ? `Graded ${a.submission.grade}` : "Submitted · waiting to be graded") : a.due_at ? `Due ${fmtDayTime(a.due_at)}` : "No Deadline"}</small></div>
          <button className="cbtn go" type="button" onClick={onAssignment}>{a.submission?.grade ? "View Feedback" : a.submission ? "View Submission" : "Open Assignment"}</button>
        </section>
      )}
      {lesson.description && (
        <section className="card about"><div className="card-h"><h3>Class Details</h3></div><p style={{ whiteSpace: "pre-wrap" }}>{lesson.description}</p></section>
      )}
      {lesson.materials?.length > 0 && (
        <section className="card">
          <div className="card-h"><h3>Materials</h3><span className="course-tag">{lesson.materials.length} {lesson.materials.length === 1 ? "file" : "files"}</span></div>
          <div>
            {lesson.materials.map((m) => (
              <div key={m.id} className="file">
                <span className="ico doc"><Icon name="files" /></span>
                <div><strong>{m.name}</strong><small>Shared by your instructor</small></div>
                <span style={{ display: "flex", gap: 4 }}>
                  <a href={m.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${m.name}`}><Icon name="eye" /></a>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="lesson-nav">
        {prev ? <button className="cbtn quiet" type="button" onClick={() => onOpen(prev)}>‹ {prev.title}</button> : <span />}
        {next && <button className="cbtn quiet" type="button" onClick={() => onOpen(next)}>{next.title} ›</button>}
      </div>
    </>
  );
}

/** Assignment screen (prototype assignScreen): facts, instructions, then submit or what was sent. */
export function AssignmentView({ lesson, onLesson, onChanged }) {
  const a = lesson.assignment;
  return (
    <>
      <div className="detail-h"><span className="kicker">Assignment · given in {lesson.weekLabel}{lesson.number ? `, Lesson ${lesson.number}` : ` · ${lesson.title}`}</span><h2>{a.title}</h2></div>
      <div className="facts">
        <div className="fact"><b>{a.due_at ? fmtDay(a.due_at) : "None"}</b><span>{a.due_at ? `Due · ${fmtTime(a.due_at)}` : "Deadline"}</span></div>
        <div className="fact"><b>{a.grace_until ? fmtDay(a.grace_until) : "—"}</b><span>Late Submissions Accepted Until</span></div>
        <div className="fact"><b>{a.submission ? (a.submission.grade || "Submitted") : "Open"}</b><span>Status</span></div>
      </div>
      <section className="card">
        <div className="card-h"><h3>Instructions</h3><button className="link-btn" type="button" onClick={onLesson}>{lesson.number ? `Go to Lesson ${lesson.number}` : "Go to the class"} ›</button></div>
        <p style={{ whiteSpace: "pre-wrap" }}>{a.description || "Your instructor will explain this assignment in class."}</p>
        {a.rubric?.length > 0 && (
          <div className="rubric-box">
            <p className="sp-lbl" style={{ margin: "6px 0" }}>How it's marked · {a.rubric.reduce((n, r) => n + r.marks, 0)} marks</p>
            {a.rubric.map((r) => <div key={r.criterion} className="rubric-row"><span>{r.criterion}</span><b>{r.marks}</b></div>)}
          </div>
        )}
        {(a.file_url || a.solution_url) && (
          <div>
            {a.file_url && <div className="file"><span className="ico doc"><Icon name="files" /></span><div><strong>Assignment File</strong><small>From your instructor</small></div><a href={a.file_url} target="_blank" rel="noopener noreferrer" aria-label="Download the assignment file"><Icon name="download" /></a></div>}
            {a.solution_url && <div className="file"><span className="ico doc"><Icon name="files" /></span><div><strong>Solution</strong><small>Released by your instructor</small></div><a href={a.solution_url} target="_blank" rel="noopener noreferrer" aria-label="Download the solution"><Icon name="download" /></a></div>}
          </div>
        )}
      </section>
      {a.submission ? <SubmittedCard submission={a.submission} note="Your instructor marks it and you'll get a notification when it's graded." />
        : a.open ? (
          <>
            <SubmissionForm kind="assignment" id={a.id} forLabel="Assignment" forValue={a.title} onSubmitted={onChanged} />
            <div className="status-line"><small style={{ color: "var(--muted)" }}>Each assignment is submitted once.{a.grace_until ? " Late work is accepted during the grace period." : ""}</small></div>
          </>
        ) : <section className="complete-box"><div><strong>Submissions are closed</strong><small>The deadline for this assignment has passed.</small></div></section>}
    </>
  );
}
